import {
  defaultRangeExtractor,
  observeElementRect,
  type Range,
  useVirtualizer,
} from "@tanstack/react-virtual";
import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cn, foldTitle } from "@/lib/utils";

/** What the tree needs of a row: the ARIA tree pattern's level, position and state. */
export type TreeViewRow = {
  id: string;
  level: number;
  setSize: number;
  posInSet: number;
  /** Set on branches (sections, folders): whether their children are shown. */
  expanded?: boolean;
};

/** Rows rendered whole up to this many; above, only the viewport and an overscan (design §5). */
export const VIRTUALIZE_ABOVE = 200;
const TYPE_AHEAD_MS = 500;

export type TreeViewProps<R extends TreeViewRow> = {
  /** The tree's accessible name. */
  label: string;
  rows: readonly R[];
  /** The row holding the tab stop; the first row when null or not shown. */
  focusedId: string | null;
  onFocusChange: (id: string) => void;
  onToggle: (row: R, open: boolean) => void;
  /** Enter on a row, or a click on it. */
  onActivate: (row: R, via: "keyboard" | "pointer") => void;
  /** `*`: expand every sibling branch of the row. */
  onExpandSiblings: (row: R) => void;
  /** Shift+F10 or the context menu key: the row's action menu, if it has one. */
  onMenu?: (row: R) => void;
  /** Left on a first-level row: back out of a drilled root (narrow screens). */
  onBack?: () => void;
  /** The text type-ahead matches against. */
  nameOf: (row: R) => string;
  /** Extra attributes of the treeitem (its accessible description, selection). */
  itemProps?: (row: R) => {
    "aria-labelledby"?: string;
    "aria-describedby"?: string;
    "aria-selected"?: boolean;
    "data-menu"?: "open";
  };
  /** A row to scroll to and focus (after a write revealed it); `at` makes each request new. */
  reveal?: { id: string; at: number } | null;
  /** Estimated row height in px (rows are measured once rendered). */
  estimateSize: (row: R) => number;
  renderRow: (row: R, state: { focused: boolean }) => ReactNode;
  /** Classes of each treeitem (indentation and hover are the caller's). */
  rowClassName?: (row: R) => string;
  /** The scroll container when it is not the tree itself (the tree then grows with its rows). */
  scrollRef?: RefObject<HTMLElement | null>;
  className?: string;
  /** Rendered after the rows inside the tree's scroll area, outside the tree (notes). */
  after?: ReactNode;
};

/**
 * An ARIA treeview (WAI-ARIA APG tree pattern) over a flat list of visible rows: one tab stop
 * with a roving `tabindex`, `aria-level/setsize/posinset/expanded` on every row (so a
 * virtualised list keeps exact positions), and the pattern's keys: Up and Down, Right (expand,
 * or first child), Left (collapse, or parent), Home and End, Enter, `*`, type-ahead, and
 * Shift+F10 or the context menu key for the row's menu. Above {@link VIRTUALIZE_ABOVE} rows only
 * the viewport is rendered, always with the focused row.
 */
export function TreeView<R extends TreeViewRow>(props: TreeViewProps<R>) {
  const { rows } = props;
  const own = useRef<HTMLDivElement>(null);
  const treeRef = useRef<HTMLDivElement>(null);
  const scroller = props.scrollRef ?? own;
  // Where the tree starts inside a shared scroll container (the page's, on the project home).
  const [margin, setMargin] = useState(0);
  // Measured only for a virtualised tree (the one reader of the margin), after each render.
  const measure = props.scrollRef !== undefined && rows.length > VIRTUALIZE_ABOVE;
  useLayoutEffect(() => {
    const box = scroller.current;
    const at = treeRef.current;
    if (!measure || !box || !at) {
      return;
    }
    const top = at.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    setMargin((was) => (Math.abs(was - top) < 1 ? was : top));
  });
  const focusIndex = Math.max(
    0,
    rows.findIndex((r) => r.id === props.focusedId),
  );
  const focused = rows[focusIndex];
  const virtual = rows.length > VIRTUALIZE_ABOVE;
  // Set by a keyboard move: once rendered, the new row takes focus and scrolls into view.
  const moved = useRef(false);
  const typed = useRef({ text: "", at: 0 });
  const elements = useRef(new Map<string, HTMLElement>());

  const estimate = props.estimateSize;
  const virtualizer = useVirtualizer({
    count: virtual ? rows.length : 0,
    getScrollElement: () => scroller.current,
    // Without layout (tests, a sheet still opening) the viewport reads 0: assume the window's.
    observeElementRect: (instance, cb) =>
      observeElementRect(instance, (rect) =>
        cb(rect.height ? rect : { width: rect.width, height: window.innerHeight || 600 }),
      ),
    estimateSize: (i) => (rows[i] ? estimate(rows[i]) : 32),
    getItemKey: (i) => rows[i]?.id ?? i,
    overscan: 12,
    scrollMargin: margin,
    // The focused row is always in the DOM, so focus never lands on a missing element.
    rangeExtractor: useCallback(
      (range: Range) => {
        const indexes = defaultRangeExtractor(range);
        return indexes.includes(focusIndex) || focusIndex >= range.count
          ? indexes
          : [...indexes, focusIndex].sort((a, b) => a - b);
      },
      [focusIndex],
    ),
    // Without layout (tests, hidden sheets) a row measures 0: keep the estimate.
    measureElement: (el, _entry, instance) => {
      const height = el.getBoundingClientRect().height;
      const index = instance.indexFromElement(el);
      return height || (rows[index] ? estimate(rows[index]) : 32);
    },
  });

  const move = (index: number) => {
    const row = rows[Math.min(Math.max(index, 0), rows.length - 1)];
    // Staying on the focused row (Up on the first, End on the last) is no move: a flag left
    // set would pull focus to the tree on a later, unrelated change of rows.
    if (row && row.id !== focused?.id) {
      moved.current = true;
      props.onFocusChange(row.id);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: after a keyboard move only
  useLayoutEffect(() => {
    if (!moved.current || !focused) {
      return;
    }
    moved.current = false;
    if (virtual) {
      virtualizer.scrollToIndex(focusIndex, { align: "auto" });
    }
    const el = elements.current.get(focused.id);
    el?.focus({ preventScroll: virtual });
    if (!virtual) {
      el?.scrollIntoView?.({ block: "nearest" });
    }
  }, [focused?.id, focusIndex]);

  const revealed = useRef(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: on a new reveal request only
  useLayoutEffect(() => {
    const index = props.reveal ? rows.findIndex((r) => r.id === props.reveal?.id) : -1;
    // Each request once, as soon as its row is shown (the tree may refetch first).
    if (index < 0 || !props.reveal || revealed.current === props.reveal.at) {
      return;
    }
    revealed.current = props.reveal.at;
    if (virtual) {
      virtualizer.scrollToIndex(index, { align: "auto" });
    }
    const el = elements.current.get(props.reveal.id);
    el?.focus({ preventScroll: virtual });
    if (!virtual) {
      el?.scrollIntoView?.({ block: "nearest" });
    }
  }, [props.reveal?.at, rows.length]);

  const parentIndex = (index: number) => {
    const level = rows[index]?.level ?? 1;
    for (let i = index - 1; i >= 0; i--) {
      if ((rows[i]?.level ?? 0) < level) {
        return i;
      }
    }
    return -1;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.getAttribute("role") !== "treeitem" || !focused) {
      return;
    }
    const i = focusIndex;
    const row = focused;
    const branch = row.expanded !== undefined;
    let handled = true;
    if ((event.key === "F10" && event.shiftKey) || event.key === "ContextMenu") {
      props.onMenu?.(row);
    } else if (event.altKey || event.ctrlKey || event.metaKey) {
      handled = false;
    } else if (event.key === "ArrowDown") {
      move(i + 1);
    } else if (event.key === "ArrowUp") {
      move(i - 1);
    } else if (event.key === "Home") {
      move(0);
    } else if (event.key === "End") {
      move(rows.length - 1);
    } else if (event.key === "ArrowRight") {
      if (branch && !row.expanded) {
        props.onToggle(row, true);
      } else if (branch && (rows[i + 1]?.level ?? 0) > row.level) {
        move(i + 1);
      }
    } else if (event.key === "ArrowLeft") {
      if (branch && row.expanded) {
        props.onToggle(row, false);
      } else if (row.level > 1) {
        move(parentIndex(i));
      } else {
        props.onBack?.();
      }
    } else if (event.key === "Enter") {
      props.onActivate(row, "keyboard");
    } else if (event.key === "*") {
      props.onExpandSiblings(row);
    } else if (event.key.length === 1 && event.key !== " ") {
      // Type-ahead: the next visible row whose name starts with the typed text.
      const now = Date.now();
      const text =
        (now - typed.current.at < TYPE_AHEAD_MS ? typed.current.text : "") + foldTitle(event.key);
      typed.current = { text, at: now };
      const start = text.length === 1 ? i + 1 : i;
      for (let k = 0; k < rows.length; k++) {
        const candidate = rows[(start + k) % rows.length];
        if (candidate && foldTitle(props.nameOf(candidate)).startsWith(text)) {
          move((start + k) % rows.length);
          break;
        }
      }
    } else {
      handled = false;
    }
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const item = (row: R, index: number, style?: CSSProperties) => {
    const isFocused = index === focusIndex;
    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents: the tree's keydown handler serves every row.
      <div
        key={row.id}
        ref={(el) => {
          if (el) {
            elements.current.set(row.id, el);
            if (virtual) {
              virtualizer.measureElement(el);
            }
          } else {
            elements.current.delete(row.id);
          }
        }}
        data-index={index}
        data-tree-row={row.id}
        role="treeitem"
        aria-level={row.level}
        aria-setsize={row.setSize}
        aria-posinset={row.posInSet}
        aria-expanded={row.expanded}
        {...props.itemProps?.(row)}
        tabIndex={isFocused ? 0 : -1}
        style={style}
        onFocus={(event) => {
          if (event.target === event.currentTarget && !isFocused) {
            props.onFocusChange(row.id);
          }
        }}
        onClick={(event) => {
          // A control inside the row (its menu, a link) does its own thing, and so does a
          // click in a portal (an open menu), which React bubbles here outside the row's DOM.
          const target = event.target as HTMLElement;
          if (
            !event.currentTarget.contains(target) ||
            target.closest("a, button, [role=menuitem]")
          ) {
            return;
          }
          props.onFocusChange(row.id);
          props.onActivate(row, "pointer");
        }}
        className={cn(
          "group/item outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
          props.rowClassName?.(row),
        )}
      >
        {props.renderRow(row, { focused: isFocused })}
      </div>
    );
  };

  const tree = (
    <div
      ref={treeRef}
      role="tree"
      aria-label={props.label}
      onKeyDown={onKeyDown}
      className="outline-none"
    >
      {virtual ? (
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((v) => {
            const row = rows[v.index];
            return row
              ? item(row, v.index, {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${v.start - margin}px)`,
                })
              : null;
          })}
        </div>
      ) : (
        rows.map((row, index) => item(row, index))
      )}
    </div>
  );

  if (props.scrollRef) {
    return (
      <div className={props.className}>
        {tree}
        {props.after}
      </div>
    );
  }
  return (
    <div ref={own} className={cn("min-h-0 overflow-y-auto", props.className)}>
      {tree}
      {props.after}
    </div>
  );
}

/** A rendered row of a tree inside `root`, by its ID (to return focus to it). */
export function treeRowElement(root: ParentNode | null, id: string): HTMLElement | null {
  return root?.querySelector<HTMLElement>(`[data-tree-row="${CSS.escape(id)}"]`) ?? null;
}
